import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ContactForm } from '@metlas/contact';
import styles from './ChatBot.module.css';

const ROTATING_PHRASES = [
  'tailored to your business context.',
  'handling edge cases.',
  'zero hallucinations and bias.',
  '24/7 support with human agent escalation.',
];

const ChatBot: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const savedVolume = localStorage.getItem('videoVolume');
    if (savedVolume !== null) video.volume = parseFloat(savedVolume);

    const handleVolumeChange = () => {
      localStorage.setItem('videoVolume', String(video.volume));
    };
    video.addEventListener('volumechange', handleVolumeChange);
    return () => video.removeEventListener('volumechange', handleVolumeChange);
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const interval = setInterval(() => {
      setIsFading(true);
      timeoutId = setTimeout(() => {
        setPhraseIndex((prev) => (prev + 1) % ROTATING_PHRASES.length);
        setIsFading(false);
      }, 400);
    }, 2000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.bg}>
        <div className={`${styles.nebula} ${styles.nebula1}`} />
        <div className={`${styles.nebula} ${styles.nebula2}`} />
        <div className={`${styles.nebula} ${styles.nebula3}`} />
      </div>

      <div className={`${styles.content} ${isVisible ? styles.visible : ''}`}>
        <nav className={styles.nav}>
          <Link to="/" className={styles.back}>
            ← Back to Metlas
          </Link>
        </nav>

        <section className={styles.hero}>
          <h1 className={styles.headline}>
            <span className={styles.headlineLine}>Finally an AI customer-experience agent you can trust,</span>
            <span className={styles.headlineLine}>built with award-winning research from Columbia and Stanford University,</span>
            <span className={`${styles.rotating} ${isFading ? styles.fadeOut : styles.fadeIn}`}>
              {ROTATING_PHRASES[phraseIndex]}
            </span>
          </h1>
        </section>

        <section className={styles.caseStudy}>
          <h2 className={styles.caseStudyTitle}>Demo: Advising Chat-Bot at Columbia University Computer Science Department</h2>
          <div className={styles.videoWrapper}>
            <video ref={videoRef} className={styles.video} src="/videos/columbia_demo.mp4" autoPlay loop muted playsInline controls>
              Your browser does not support the video tag.
            </video>
          </div>
        </section>
      </div>

      <aside className={styles.contactFloat}>
        <ContactForm variant="float" />
      </aside>
    </div>
  );
};

export default ChatBot;
